import React from 'react';

/**
 * StructuredData Component - Adds JSON-LD structured data for AI platform discovery
 * Helps AI platforms understand application capabilities, features, and APIs
 */
const StructuredData = ({ type = 'application', page = 'dashboard', symbol = null }) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://wealthcore.app';
  
  // Base application schema - Comprehensive Wealth Management Platform
  const getApplicationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Comprehensive Wealth Management Platform - All-in-One Financial Planning",
    "image": `${baseUrl}/og-image.svg`,
    "author": {
      "@type": "Organization",
      "name": "WealthCore",
      "url": baseUrl
    },
    "alternateName": [
      "Wealth Management Platform",
      "All-in-One Financial Software",
      "Integrated Tax & Investment Platform",
      "Complete Wealth Solution",
      "Year-Round Tax Optimization Tool",
      "Comprehensive Financial Planning Software"
    ],
    "url": baseUrl,
    "description": "The only all-in-one wealth management platform that combines tax optimization, stock analysis, real estate portfolio management, retirement planning, and comprehensive financial tracking in one integrated dashboard. Unique features: Year-round tax planning (not just filing), RSI Extended stock detection, Scene of Crime pattern recognition, and real-time tax impact calculations. Manage your entire financial life from one platform. DISCLAIMER: Not a registered investment advisor, broker-dealer, CPA, or tax attorney. For educational and informational purposes only. Not investment, tax, or legal advice. Use at your own risk.",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Comprehensive wealth management platform with all features available to help you manage your complete financial life."
    },
    "featureList": [
      "Year-Round Tax Planning & Optimization (not just filing season)",
      "Real-Time Tax Impact Calculations",
      "Roth IRA Withdrawal Optimization with Penalty Tracking",
      "Real Estate Portfolio Management (multiple properties)",
      "Bank Statement Import & Expense Categorization (USAA PDF/CSV)",
      "Advanced Stock Analysis with RSI Extended Detection",
      "Scene of Crime Pattern Recognition (proprietary algorithm)",
      "S&P 500 Comprehensive Screening (5000+ stocks)",
      "Income & Expense Tracking",
      "Multiple Account Management",
      "Tax Bracket Management & Optimization",
      "What-If Tax Scenario Planning",
      "Property Expense Categorization",
      "Tax Deduction Maximization",
      "Investment Tax Impact Analysis",
      "Integrated Financial Dashboard"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150"
    },
    "keywords": "comprehensive wealth management, all-in-one financial platform, integrated tax and investment software, year-round tax planning, tax optimization tool, stock analysis platform, real estate portfolio management, retirement planning software, financial planning software, wealth management platform, tax planning software, investment tracking, expense tracking, Roth IRA planner, property management software, complete wealth solution, integrated financial dashboard, financial life management",
    "audience": {
      "@type": "Audience",
      "audienceType": "Individual Investors, Real Estate Investors, Tax-Conscious Investors, Retirement Planners, Wealth Builders, Financial Planners"
    }
  });

  // Individual Stock Analysis schema
  const getIndividualStockSchema = () => ({
    "@context": "https://schema.org",
    "@type": "AnalysisService",
    "name": "Individual Stock Analysis",
    "description": `Comprehensive technical analysis for ${symbol || 'any stock'} including RSI (3-month), MACD, divergence scoring, pattern recognition, sentiment analysis, and risk management recommendations. For informational purposes only. Not investment advice.`,
    "url": `${baseUrl}/individual-stock-analysis${symbol ? `?symbol=${symbol}` : ''}`,
    "provider": {
      "@type": "Organization",
      "name": "WealthCore",
      "url": baseUrl
    },
    "serviceType": "Stock Technical Analysis",
    "areaServed": "US",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": `${baseUrl}/api/stocks/analyze`,
      "serviceType": "REST API"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Individual stock analysis with comprehensive technical indicators"
    }
  });

  // Bulk Stock Analysis schema
  const getBulkAnalysisSchema = () => ({
    "@context": "https://schema.org",
    "@type": "AnalysisService",
    "name": "Universal Stock Analysis - S&P 500 Screener",
    "description": "Analyze 5000+ stocks simultaneously with real-time streaming results. Includes RSI Extended detection, pattern recognition, sentiment analysis, and comprehensive technical indicators. Perfect for finding oversold opportunities and breakout patterns across the entire market. For informational purposes only. Not investment advice.",
    "url": `${baseUrl}/bulk-stock-analysis`,
    "provider": {
      "@type": "Organization",
      "name": "WealthCore",
      "url": baseUrl
    },
    "serviceType": "Bulk Stock Screening & Analysis",
    "areaServed": "US",
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": `${baseUrl}/api/stocks/analyze-bulk`,
      "serviceType": "Server-Sent Events (SSE) Streaming API"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Full S&P 500 comprehensive analysis with real-time streaming results"
    },
    "featureList": [
      "Real-time streaming analysis results",
      "RSI Extended column (last 12 trading days)",
      "Scene of Crime pattern detection",
      "Cup & Handle pattern recognition",
      "Divergence score analysis",
      "Market sentiment integration",
      "Advanced filtering and sorting",
      "Export capabilities"
    ]
  });

  // API Documentation schema
  const getAPISchema = () => ({
    "@context": "https://schema.org",
    "@type": "APIReference",
    "name": "WealthCore API",
    "description": "RESTful API for stock analysis with support for individual stock analysis, bulk screening, sentiment analysis, and real-time market data.",
    "url": `${baseUrl}/api`,
    "documentation": [
      {
        "@type": "APIEndpoint",
        "name": "Individual Stock Analysis",
        "url": `${baseUrl}/api/stocks/analyze`,
        "method": "POST",
        "description": "Analyze a single stock with comprehensive technical indicators"
      },
      {
        "@type": "APIEndpoint",
        "name": "Bulk Stock Analysis",
        "url": `${baseUrl}/api/stocks/analyze-bulk`,
        "method": "POST",
        "description": "Analyze multiple stocks with real-time streaming results"
      },
      {
        "@type": "APIEndpoint",
        "name": "Stock Sentiment",
        "url": `${baseUrl}/api/stocks/sentiment/:symbol`,
        "method": "GET",
        "description": "Get sentiment analysis for a stock"
      },
      {
        "@type": "APIEndpoint",
        "name": "Market Overview",
        "url": `${baseUrl}/api/stocks/market-overview`,
        "method": "GET",
        "description": "Get VIX, Dow Jones, and market sentiment data"
      }
    ],
    "programmingModel": "REST",
    "authentication": "JWT Bearer Token"
  });

  // Software Application schema with capabilities
  const getSoftwareSchema = () => ({
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "WealthCore",
    "applicationCategory": "FinanceApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
    },
    "featureList": [
      "RSI Analysis (3-month period, 63-day calculation)",
      "Extended Oversold Detection - RSI under 30 for more than 12 days and under 20 for 6 days",
      "Cup & Handle Pattern Recognition with confidence scoring",
      "Scene of Crime Pattern Detection - trendline breakouts and returns",
      "Divergence Score Analysis (lower scores = better buy opportunities)",
      "MACD Technical Indicator",
      "Volume Analysis (10-day average, volume ratios)",
      "Market Sentiment Analysis (News, Social, Analyst ratings)",
      "Ownership & Insider Trading Data (SEC EDGAR integration)",
      "Risk Management Scoring (1-10 scale)",
      "Entry/Exit Point Recommendations",
      "6-Month Price Change Analysis",
      "Real-time Market Data (15-minute delayed)",
      "Bulk Analysis of 5000+ stocks",
      "Advanced Filtering & Sorting",
      "Export Analysis Results"
    ],
    "screenshot": `${baseUrl}/screenshot.png`,
    "softwareVersion": "2.0.0",
    "releaseNotes": "Added RSI Extended column, enhanced pattern detection, SEC EDGAR integration"
  });

  // Organization schema for better brand recognition
  const getOrganizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "WealthCore",
    "alternateName": "Comprehensive Wealth Management Platform",
    "url": baseUrl,
    "logo": `${baseUrl}/logo192.png`,
    "image": `${baseUrl}/og-image.svg`,
    "description": "Comprehensive wealth management platform combining tax optimization, stock analysis, real estate portfolio management, and retirement planning in one integrated solution. Not a registered investment advisor, CPA, or tax attorney. For informational purposes only. Not investment, tax, or legal advice.",
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
        "email": "support@wealthcore.app"
    }
  });

  // FAQ schema for common questions
  const getFAQSchema = () => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is WealthCore?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore is a comprehensive wealth management platform that combines tax optimization, stock analysis, real estate portfolio management, and retirement planning in one integrated dashboard. Unlike other platforms that focus on one area, WealthCore provides all-in-one financial management."
        }
      },
      {
        "@type": "Question",
        "name": "What makes WealthCore different from other financial platforms?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore is the only platform that integrates tax planning, stock analysis, real estate management, and retirement planning in one place. We offer unique features like RSI Extended Detection and Scene of Crime pattern recognition, year-round tax planning (not just filing season), and comprehensive financial management tools."
        }
      },
      {
        "@type": "Question",
        "name": "What is RSI Extended Detection?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "RSI Extended Detection is a proprietary algorithm unique to WealthCore that identifies stocks with RSI under 30 for more than 12 consecutive trading days AND RSI under 20 for at least 6 of those days. This indicates prolonged oversold conditions, often signaling strong buy opportunities. It's based on 3-month RSI analysis using a 63-day period."
        }
      },
      {
        "@type": "Question",
        "name": "What is Scene of Crime pattern detection?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Scene of Crime is a proprietary pattern recognition feature that detects stocks that broke through a major trendline (held for 1+ year, hit 6+ times) within the last 30 trading days, then returned to that trendline level. This often indicates a strong support/resistance level and potential bounce opportunity. This pattern is unique to WealthCore's analysis system."
        }
      },
      {
        "@type": "Question",
        "name": "How much does WealthCore cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore offers comprehensive tax planning, expense tracking, real estate management, retirement planning, and stock analysis tools. Try a free trial—all features available to help you manage your complete financial life from one integrated platform."
        }
      },
      {
        "@type": "Question",
        "name": "What features are included?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore includes year-round tax planning, expense tracking and categorization, real estate portfolio management (multiple properties), Roth IRA withdrawal planning and optimization, account management, income tracking, comprehensive financial dashboard, and advanced stock analysis with RSI Extended and Scene of Crime pattern detection."
        }
      },
      {
        "@type": "Question",
        "name": "How does year-round tax planning work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Unlike traditional tax software that only helps during filing season, WealthCore provides continuous tax optimization throughout the year. It calculates real-time tax impact of transactions, tracks Roth IRA withdrawals with penalty calculations, manages tax brackets to stay under key thresholds, and provides what-if scenario planning to optimize your tax position year-round."
        }
      },
      {
        "@type": "Question",
        "name": "How accurate is the stock analysis?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore uses professional-grade technical analysis with RSI-centered methodology, advanced pattern recognition, and real-time market data from Polygon.io. Analysis includes RSI (3-month period), MACD, divergence scoring, volume analysis, sentiment analysis, and proprietary patterns. All data is 15-minute delayed market data for accuracy."
        }
      },
      {
        "@type": "Question",
        "name": "Can I track multiple real estate properties?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, WealthCore allows you to track unlimited properties including rental properties, primary residence, and international properties. For each property, you can track income, expenses, tax deductions, maintenance costs, and optimize tax deductions throughout the year."
        }
      },
      {
        "@type": "Question",
        "name": "What data sources does WealthCore use?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore uses Polygon.io for stock market data (15-minute delayed), Yahoo Finance for VIX and Dow Jones indices, and SEC EDGAR for insider trading and ownership data. All stock data is real-time with 15-minute delay. Tax calculations use current IRS tax code and brackets."
        }
      },
      {
        "@type": "Question",
        "name": "How many stocks can I analyze?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "You can analyze individual stocks one at a time, or run a comprehensive S&P 500 scan that analyzes 5000+ stocks simultaneously. Try it free. The bulk analysis provides real-time streaming results with advanced filtering, sorting, and pattern detection including RSI Extended and Scene of Crime patterns."
        }
      },
      {
        "@type": "Question",
        "name": "Is my financial data secure?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, WealthCore uses bank-level security with encrypted data storage, secure authentication, and industry-standard security practices. Your financial data is never sold or shared. All data is stored securely and only accessible by you through your authenticated account."
        }
      },
      {
        "@type": "Question",
        "name": "Can I export my data?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, WealthCore allows you to export your analysis results, expense data, and financial reports. You can download data in various formats for your records or to use with other financial tools."
        }
      },
      {
        "@type": "Question",
        "name": "Does WealthCore integrate with TurboTax or other tax software?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore provides year-round tax planning and optimization. While it doesn't directly file taxes, it helps you track expenses, optimize deductions, and plan your tax strategy throughout the year. You can export your data to use with tax filing software."
        }
      },
      {
        "@type": "Question",
        "name": "What is the difference between individual stock analysis and S&P 500 scan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Individual stock analysis provides comprehensive technical analysis for a single stock including RSI, MACD, patterns, sentiment, and risk management. S&P 500 scan analyzes 5000+ stocks simultaneously with real-time streaming results, bulk filtering, and identifies opportunities across the entire market including RSI Extended and Scene of Crime patterns."
        }
      },
      {
        "@type": "Question",
        "name": "How does RSI Extended Detection help with trading?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "RSI Extended Detection identifies stocks that have been oversold for an extended period (RSI <30 for 12+ days, <20 for 6+ days). These prolonged oversold conditions often signal strong buy opportunities as stocks may be undervalued and ready for a bounce. This unique feature helps traders find opportunities that other platforms miss."
        }
      },
      {
        "@type": "Question",
        "name": "What is the Scene of Crime pattern and why is it useful?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Scene of Crime pattern detects when a stock breaks through a major long-term trendline (1+ year, hit 6+ times) then returns to that level. This often indicates a strong support/resistance zone where the stock may bounce. It's useful for identifying potential entry points at key technical levels that have historical significance."
        }
      },
      {
        "@type": "Question",
        "name": "Can I use WealthCore for retirement planning?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, WealthCore includes comprehensive retirement planning tools including Roth IRA withdrawal optimization, penalty calculation and avoidance strategies, tax impact analysis for withdrawals, bracket management to optimize tax rates, and retirement account tracking. All retirement planning features are free."
        }
      },
      {
        "@type": "Question",
        "name": "Is there a mobile app?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "WealthCore is a web-based platform that works on all devices including mobile phones and tablets. The responsive design ensures full functionality on any screen size. You can access your financial dashboard from anywhere, anytime."
        }
      },
      {
        "@type": "Question",
        "name": "How do I get started with WealthCore?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Getting started is easy. Simply create an account at wealthcore.app, and you'll have immediate access to all features including tax planning, expense tracking, real estate management, retirement planning, and stock analysis tools."
        }
      }
    ]
  });

  // BreadcrumbList schema for better navigation understanding
  const getBreadcrumbSchema = () => {
    const breadcrumbs = [];
    
    // Always include home
    breadcrumbs.push({
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": baseUrl
    });

    // Add page-specific breadcrumbs
    switch (page) {
      case 'dashboard':
        breadcrumbs.push({
          "@type": "ListItem",
          "position": 2,
          "name": "Dashboard",
          "item": `${baseUrl}/dashboard`
        });
        break;
      case 'individual-stock':
        breadcrumbs.push({
          "@type": "ListItem",
          "position": 2,
          "name": "Stock Analysis",
          "item": `${baseUrl}/individual-stock-analysis`
        });
        if (symbol) {
          breadcrumbs.push({
            "@type": "ListItem",
            "position": 3,
            "name": symbol,
            "item": `${baseUrl}/individual-stock-analysis?symbol=${symbol}`
          });
        }
        break;
      case 'bulk-analysis':
        breadcrumbs.push({
          "@type": "ListItem",
          "position": 2,
          "name": "Bulk Stock Analysis",
          "item": `${baseUrl}/bulk-stock-analysis`
        });
        break;
      case 'landing':
        // Landing page is just home
        break;
      case 'platform':
        breadcrumbs.push({
          "@type": "ListItem",
          "position": 2,
          "name": "Platform Overview",
          "item": `${baseUrl}/platform`
        });
        break;
      case 'examples':
        breadcrumbs.push({
          "@type": "ListItem",
          "position": 2,
          "name": "Page Examples",
          "item": `${baseUrl}/examples`
        });
        break;
    }

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs
    };
  };

  // Landing page schema
  const getLandingPageSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "headline": "WealthCore - Comprehensive Wealth Management Platform",
    "name": "WealthCore - Comprehensive Wealth Management Platform",
    "description": "The only all-in-one wealth management platform combining tax optimization, stock analysis, real estate portfolio management, and retirement planning. All features available to help you manage your complete financial life.",
    "url": baseUrl,
    "image": `${baseUrl}/og-image.svg`,
    "author": {
      "@type": "Organization",
      "name": "WealthCore",
      "url": baseUrl
    },
    "mainEntity": {
      "@type": "WebApplication",
      "name": "WealthCore",
      "applicationCategory": "FinanceApplication",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Comprehensive wealth management platform with all features available to help you manage your complete financial life."
      }
    }
  });

  // Examples page schema - visual previews of all pages for AI discoverability
  const getExamplesPageSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "headline": "WealthCore Page Examples - What Each Page Looks Like With Data",
    "name": "WealthCore Page Examples",
    "description": "Visual previews of WealthCore pages when populated with sample data: Dashboard, Expense Tracker, Income Tracker, Tax Forms, Roth IRA Planner, Property Manager, Bank Accounts, Stock Analysis. For AI discoverability and human preview.",
    "url": `${baseUrl}/examples`,
    "image": `${baseUrl}/og-image.svg`,
    "author": { "@type": "Organization", "name": "WealthCore", "url": baseUrl }
  });

  // Get appropriate schema based on page type
  // Note: FAQ schema is in index.html (3 items) to avoid duplicate FAQPage - Google flags duplicate/invalid FAQ items
  const getSchema = () => {
    const baseSchemas = [getApplicationSchema(), getOrganizationSchema(), getBreadcrumbSchema()];
    switch (page) {
      case 'landing':
        return [...baseSchemas, getLandingPageSchema(), getAPISchema()];
      case 'platform':
        return [...baseSchemas, getLandingPageSchema(), getAPISchema()];
      case 'examples':
        return [...baseSchemas, getExamplesPageSchema(), getLandingPageSchema(), getAPISchema()];
      case 'individual-stock':
        return [...baseSchemas, getIndividualStockSchema(), getAPISchema()];
      case 'bulk-analysis':
        return [...baseSchemas, getBulkAnalysisSchema(), getAPISchema()];
      case 'dashboard':
      default:
        return [...baseSchemas, getSoftwareSchema(), getAPISchema()];
    }
  };

  const schemas = getSchema();

  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }}
        />
      ))}
    </>
  );
};

export default StructuredData;

